/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import type { ShopperSearch } from '@/scapi';
import type { CategoryProductsResult, loader as categoryProductsLoader } from '@/routes/resource.category-products';
import { resourceRoutes } from '@/route-paths';
import { PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';

type ProductSearchHit = ShopperSearch.schemas['ProductSearchHit'];

/**
 * Narrow the raw `fetcher.data` (which is `unknown`-ish once the resource route can return either a
 * JSON payload or a bare error Response) to our result shape, or `null` if it isn't one. A non-null
 * return means the last request succeeded.
 */
function asCategoryProductsResult(data: unknown): CategoryProductsResult | null {
    if (!data || typeof data !== 'object') {
        return null;
    }
    const candidate = data as Partial<CategoryProductsResult>;
    if (!Array.isArray(candidate.hits) || typeof candidate.offset !== 'number') {
        return null;
    }
    return candidate as CategoryProductsResult;
}

/** Viewport (max-)width below which the mobile batch size applies. Matches the Tailwind `md` breakpoint. */
const MOBILE_QUERY = '(max-width: 767px)';

/**
 * @property refine - The active search refinements (`cgid=...`, plus any facet refinements).
 * @property sort - The active sorting option id, if any.
 * @property currency - Currency to price the additional hits in.
 * @property initialCount - How many products the route loader *expects* to have rendered on the first
 *   page (critical + non-critical, assuming the non-critical batch succeeds). Used as the optimistic
 *   starting offset until `nonCriticalHits` resolves and corrects it — see `criticalCount` below.
 * @property initialDisplayTarget - Minimum number of products the first listing view should contain.
 *   This can exceed `initialCount` on mobile, where the server streams a smaller initial window for
 *   performance and the client fills the remainder near the end of that window.
 * @property criticalCount - How many products the loader's critical (awaited) batch actually returned.
 *   Combined with the resolved length of `nonCriticalHits`, this gives the true first-page count.
 * @property nonCriticalHits - The route's deferred non-critical batch. The loader races this against a
 *   timeout and degrades to an empty array on failure/timeout (see `_app.category.$categoryId.tsx`) —
 *   when that happens, fewer products actually rendered than `initialCount` assumed. This hook awaits
 *   the same promise to learn the true count before allowing "load more" to fire, so a fetch never
 *   starts from `offset + initialCount` when the real, on-screen count is lower (which would otherwise
 *   permanently skip the un-rendered middle window).
 * @property total - Total number of products matching the current search.
 * @property batchSize - Products to request per batch on desktop/tablet viewports.
 * @property mobileBatchSize - Products to request per batch on mobile viewports (smaller for perf).
 * @property maxProducts - DOM cap: once this many products are loaded, `capReached` is set and no
 *   further batches load (the UI prompts the shopper to refine filters instead).
 * @property identity - A stable string that changes whenever the underlying query changes (category,
 *   sort, refinements, locale, currency). When it changes, accumulated batches are discarded so the
 *   shopper starts from the fresh first page again.
 */
export interface UseLoadMoreProductsOptions {
    refine: string[];
    sort?: string;
    currency?: string;
    initialCount: number;
    initialDisplayTarget: number;
    criticalCount: number;
    nonCriticalHits: Promise<ProductSearchHit[]>;
    total: number;
    batchSize: number;
    mobileBatchSize: number;
    maxProducts: number;
    identity: string;
    offset?: number;
}

/**
 * @property appended - Products loaded after the initial page, to render below the initial grid tiles.
 * @property loadedCount - How many products are currently rendered (initial page + appended).
 * @property total - Total number of products matching the current search.
 * @property hasMore - Whether more products remain to load AND the DOM cap has not been hit.
 * @property capReached - Whether the DOM cap (`maxProducts`) has been reached while products still remain.
 * @property isLoading - Whether a "load more" request is in flight.
 * @property hasError - Whether the most recent "load more" request failed.
 * @property firstNewIndex - Index within `appended` at which the most recent batch begins, or `null`.
 *   Consumers use it to move focus to the first newly appended tile after a load (accessibility).
 * @property needsInitialFill - Whether the rendered window is below `initialDisplayTarget` and should
 *   be filled automatically as the load-more control approaches the viewport.
 * @property loadMore - Request the next batch. No-op while loading, at the cap, or when nothing remains.
 */
export interface UseLoadMoreProductsResult {
    appended: ProductSearchHit[];
    loadedCount: number;
    total: number;
    hasMore: boolean;
    capReached: boolean;
    isLoading: boolean;
    isRestoring: boolean;
    restorationTarget: number;
    hasError: boolean;
    firstNewIndex: number | null;
    needsInitialFill: boolean;
    loadMore: (options?: { moveFocus?: boolean }) => void;
}

/**
 * Client-side accumulation for the product listing "Load more" control.
 *
 * The first batch of products is server-rendered by the route loader (critical + non-critical). This
 * hook appends further batches by calling the `/resource/category-products` endpoint with `useFetcher`
 * — a non-navigating fetch, so tiles are appended in place rather than the whole route re-rendering.
 * Accumulated batches are keyed to the query `identity`; when the shopper changes sort or refinements
 * (which changes the URL and re-runs the loader), the accumulated list resets to the new first page.
 */
export function useLoadMoreProducts({
    refine,
    sort,
    currency,
    initialCount: rawInitialCount,
    initialDisplayTarget: rawInitialDisplayTarget,
    criticalCount: rawCriticalCount,
    nonCriticalHits,
    total: rawTotal,
    batchSize,
    mobileBatchSize,
    maxProducts,
    identity,
    offset = 0,
}: UseLoadMoreProductsOptions): UseLoadMoreProductsResult {
    const fetcher = useFetcher<typeof categoryProductsLoader>();

    // RGBARRY-CUSTOMIZATION: defensive guard — every arithmetic path below derives from these three
    // numbers, so a non-finite value from an upstream data gap (a malformed search response, etc.)
    // must never propagate into `loadedCount`/`total` and render as "Showing NaN of NaN".
    const initialCount = Number.isFinite(rawInitialCount) ? rawInitialCount : 0;
    // RGBARRY-CUSTOMIZATION: fill short mobile/faulted first windows to the standard PLP target.
    const initialDisplayTarget = Number.isFinite(rawInitialDisplayTarget) ? rawInitialDisplayTarget : initialCount;
    const criticalCount = Number.isFinite(rawCriticalCount) ? rawCriticalCount : 0;
    const total = Number.isFinite(rawTotal) ? rawTotal : 0;

    // Batches appended after the initial (server-rendered) page.
    const [appended, setAppended] = useState<ProductSearchHit[]>([]);
    // Next offset to request. Starts just past the initial (server-rendered) page — corrected below
    // once `nonCriticalHits` resolves, in case fewer products actually rendered than `initialCount` assumed.
    const nextOffsetRef = useRef<number>(offset + initialCount);
    // Index within `appended` where the most recent batch starts — used to focus the first new tile.
    const [firstNewIndex, setFirstNewIndex] = useState<number | null>(null);

    // True first-page count, corrected once `nonCriticalHits` settles (starts as the optimistic
    // `initialCount` so the UI shows the expected count while the non-critical batch is still pending).
    const actualInitialCountRef = useRef<number>(initialCount);
    // Set once a real "load more" batch has actually been merged for the current query — see the merge
    // effect below. Guards the correction below from clobbering a `nextOffsetRef` that a real server
    // response has already advanced past the optimistic starting point (that response is always the
    // more authoritative source once it exists).
    const hasFetchedRef = useRef(false);
    // RGBARRY-CUSTOMIZATION: an automatic viewport fill must not move keyboard focus. Manual button
    // loads retain the existing focus behavior.
    const moveFocusForPendingBatchRef = useRef(true);
    // Gates "load more": stays false until we know how many products actually rendered, so a click
    // can never fire a request from the optimistic (possibly wrong) `offset + initialCount`.
    const [nonCriticalReady, setNonCriticalReady] = useState(false);
    useEffect(() => {
        setNonCriticalReady(false);
        actualInitialCountRef.current = initialCount;
        let cancelled = false;
        void nonCriticalHits.then((hits) => {
            if (cancelled) return;
            const actual = criticalCount + hits.length;
            actualInitialCountRef.current = actual;
            if (!hasFetchedRef.current) {
                nextOffsetRef.current = offset + actual;
            }
            setNonCriticalReady(true);
        });
        return () => {
            cancelled = true;
        };
    }, [nonCriticalHits, criticalCount, initialCount, offset]);

    // Resolve the effective batch size for the viewport. Desktop on the server / first render;
    // re-resolves after mount and on resize so a rotate/resize picks up the right size.
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const mql = globalThis.matchMedia?.(MOBILE_QUERY);
        if (!mql) {
            return;
        }
        const sync = () => setIsMobile(mql.matches);
        sync();
        mql.addEventListener('change', sync);
        return () => mql.removeEventListener('change', sync);
    }, []);
    const effectiveBatchSize = isMobile ? mobileBatchSize : batchSize;

    // Reset accumulation whenever the underlying query changes, and scroll back to the top so the
    // shopper starts the new result set from its first product (filter/sort reset behavior).
    const isFirstRenderRef = useRef(true);
    const prevIdentityRef = useRef(identity);
    useEffect(() => {
        const identityChanged = prevIdentityRef.current !== identity;
        prevIdentityRef.current = identity;

        nextOffsetRef.current = offset + initialCount;
        hasFetchedRef.current = false;
        moveFocusForPendingBatchRef.current = true;
        setAppended([]);
        setFirstNewIndex(null);

        // Don't scroll on the initial mount — only when the query actually changes after first paint.
        // Also don't scroll when initialCount changes but identity hasn't (back-nav restoration).
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            return;
        }
        if (!identityChanged) {
            return;
        }
        globalThis.scrollTo?.({ top: 0, behavior: 'smooth' });
    }, [identity, initialCount, offset]);

    // Identity the in-flight request was issued for — captured in `loadMore` and the restoration
    // effect below, right before each `fetcher.load` call. RGBARRY-CUSTOMIZATION: this route
    // component is NOT remounted on client-side category/sort/refine navigation (only the grid's
    // `key`ed subtree is), so this hook instance — and its `useFetcher` — persists across an
    // identity change. Without this guard, a "load more"/restoration request still in flight when
    // the shopper navigates to a different category lands after the reset effect has already
    // cleared `appended` for the new identity, and gets merged onto the new category's list anyway
    // — inflating `loadedCount` past `total` (observed live as "Showing 165 of 32").
    const pendingIdentityRef = useRef(identity);

    // Merge each settled batch into the accumulated list, de-duplicating by product id so a
    // shifted offset window (e.g. after a background revalidation) never renders a tile twice.
    const result = asCategoryProductsResult(fetcher.data);
    const lastMergedRef = useRef<unknown>(null);
    useEffect(() => {
        if (!result || fetcher.data === lastMergedRef.current) {
            return;
        }
        lastMergedRef.current = fetcher.data;
        // Discard a response that was requested for a different (now stale) identity — see the
        // `pendingIdentityRef` comment above.
        if (pendingIdentityRef.current !== identity) {
            return;
        }
        const batch = result.hits;
        if (batch.length === 0) {
            return;
        }
        hasFetchedRef.current = true;
        nextOffsetRef.current = result.offset + batch.length;
        setAppended((prev) => {
            const seen = new Set<string | undefined>(prev.map((h) => h.productId));
            const fresh = batch.filter((h) => !seen.has(h.productId));
            if (!fresh.length) {
                return prev;
            }
            setFirstNewIndex(moveFocusForPendingBatchRef.current ? prev.length : null);
            moveFocusForPendingBatchRef.current = true;
            return [...prev, ...fresh];
        });
    }, [fetcher.data, result, identity]);

    // Back-nav catch-up: read the prior loaded count from sessionStorage (keyed by React Router's
    // history key) synchronously so the skeleton grid renders on the very first frame — before
    // ScrollRestoration fires. useState's lazy initializer runs only once on the client.
    const STORAGE_KEY = 'sfnext:loadMore';
    const [restorationTargetInitial] = useState(() => {
        if (typeof window === 'undefined') return 0;
        try {
            const historyKey = (window.history.state as { key?: string } | null)?.key;
            if (!historyKey) return 0;
            const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
            const saved = stored[historyKey] ?? 0;
            return saved > initialCount ? saved : 0;
        } catch {
            return 0;
        }
    });
    const restorationTargetRef = useRef<number>(restorationTargetInitial);

    // Reflects `actualInitialCountRef` once known, so the count on screen is corrected downward if
    // the non-critical batch actually returned fewer hits than the loader optimistically assumed.
    const loadedCount = (nonCriticalReady ? actualInitialCountRef.current : initialCount) + appended.length;
    // DOM cap: stop offering more once `maxProducts` are loaded, even if the catalog has more.
    const capReached = loadedCount >= maxProducts && loadedCount < total;
    // Gated on `nonCriticalReady` — until the non-critical batch resolves we don't yet know the real
    // first-page count, and allowing a click before then would fetch the next batch from the
    // optimistic (possibly too-high) offset, permanently skipping whatever didn't actually render.
    const hasMore = nonCriticalReady && loadedCount < total && !capReached;
    // RGBARRY-CUSTOMIZATION: recover a failed/short deferred batch and grow the intentional 12-tile
    // mobile SSR window only to the normal first-page target before regular pagination resumes.
    const initialFillGap = Math.max(0, Math.min(initialDisplayTarget, total, maxProducts) - loadedCount);
    const needsInitialFill = hasMore && initialFillGap > 0;
    const isLoading = fetcher.state === 'loading';
    // A settled fetch whose payload is not our JSON result object (the resource route returned a
    // non-2xx Response, which useFetcher surfaces as the raw body) means the last request failed.
    // RGBARRY-CUSTOMIZATION: scope a failed fetcher payload to the query that issued it. The route
    // hook survives category/filter navigation, so an error from identity A must not suppress B's
    // one-shot initial fill after the listing has reset to identity B.
    const hasError =
        fetcher.state === 'idle' &&
        fetcher.data !== undefined &&
        result === null &&
        pendingIdentityRef.current === identity;

    const loadMore = useCallback(
        (options?: { moveFocus?: boolean }) => {
            if (isLoading || !hasMore) {
                return;
            }
            // Never request past the DOM cap: shrink the final batch so we land exactly on maxProducts.
            const remainingToCap = Math.max(0, maxProducts - nextOffsetRef.current);
            const remainingResults = Math.max(0, total - nextOffsetRef.current);
            // RGBARRY-CUSTOMIZATION: fill exactly the missing first-page gap (for example, 4 -> 24
            // requests 20) rather than appending a full batch and overshooting the displayed count.
            const requestedWindow = initialFillGap > 0 ? initialFillGap : effectiveBatchSize;
            const requestLimit = Math.min(requestedWindow, remainingToCap, remainingResults);
            if (requestLimit <= 0) {
                return;
            }
            moveFocusForPendingBatchRef.current = options?.moveFocus !== false;
            const params = new URLSearchParams();
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.OFFSET, String(nextOffsetRef.current));
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.LIMIT, String(requestLimit));
            if (sort) {
                params.set(PRODUCT_SEARCH_QUERY_PARAMS.SORT, sort);
            }
            if (currency) {
                params.set(PRODUCT_SEARCH_QUERY_PARAMS.CURRENCY, currency);
            }
            for (const r of refine) {
                params.append(PRODUCT_SEARCH_QUERY_PARAMS.REFINE, r);
            }
            pendingIdentityRef.current = identity;
            void fetcher.load(`${resourceRoutes.categoryProducts}?${params.toString()}`);
        },
        [
            isLoading,
            hasMore,
            initialFillGap,
            effectiveBatchSize,
            maxProducts,
            total,
            sort,
            currency,
            refine,
            identity,
            fetcher,
        ]
    );

    // Back-nav catch-up: fetch missing products in a single request to restore the prior depth.
    // The gap is capped at 100 to respect the resource route's per-request limit and avoid multiple
    // sequential fetches. If the user had loaded more than 124 products (initialCount + 100), they
    // will need to click "Load More" to reach the rest.
    const RESTORATION_LIMIT = 100;
    const restorationFiredRef = useRef(false);
    useEffect(() => {
        if (restorationFiredRef.current || restorationTargetRef.current === 0 || isLoading) {
            return;
        }
        restorationFiredRef.current = true;
        const gap = Math.min(restorationTargetRef.current - initialCount, RESTORATION_LIMIT);
        if (gap <= 0) {
            restorationTargetRef.current = 0;
            return;
        }
        restorationTargetRef.current = initialCount + gap;
        const params = new URLSearchParams();
        params.set(PRODUCT_SEARCH_QUERY_PARAMS.OFFSET, String(offset + initialCount));
        params.set(PRODUCT_SEARCH_QUERY_PARAMS.LIMIT, String(gap));
        if (sort) {
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.SORT, sort);
        }
        if (currency) {
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.CURRENCY, currency);
        }
        for (const r of refine) {
            params.append(PRODUCT_SEARCH_QUERY_PARAMS.REFINE, r);
        }
        pendingIdentityRef.current = identity;
        void fetcher.load(`${resourceRoutes.categoryProducts}?${params.toString()}`);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fire once on mount only

    // RGBARRY-CUSTOMIZATION: gate the "nothing more to fetch" clear on the catch-up fetch having settled,
    // so the height-reserving skeleton grid survives the whole back-nav restoration window (see below).
    //
    // Clear restoration target when: enough products loaded, fetch errored, or the catch-up fetch has
    // settled without reaching the target (catalog shrunk, or the response was discarded as stale).
    //
    // The "settled" gate is critical: `fetcher.load()` is async, so on the very first commit after a
    // back-nav the restoration fetch has been fired but `fetcher.state` has not yet flipped to
    // 'loading' and `nonCriticalHits` has not resolved (so `hasMore` is still false). A bare
    // `(!hasMore && !isLoading)` check therefore fires immediately and zeroes the target one tick
    // after it was set — collapsing the height-reserving skeleton grid before the async catch-up can
    // fill it, which strands <ScrollRestoration> against a short document. Gating on the fetch having
    // actually returned (fired + idle + data present) keeps `isRestoring` true for the whole
    // restoration window so the skeleton grid holds the scroll height until the real tiles arrive.
    const restorationFetchSettled =
        restorationFiredRef.current && fetcher.state === 'idle' && fetcher.data !== undefined;
    useEffect(() => {
        if (restorationTargetRef.current > 0) {
            if (loadedCount >= restorationTargetRef.current || hasError || restorationFetchSettled) {
                restorationTargetRef.current = 0;
            }
        }
    }, [loadedCount, hasError, restorationFetchSettled]);

    const isRestoring = restorationTargetRef.current > 0 && loadedCount < restorationTargetRef.current;

    return {
        appended,
        loadedCount,
        total,
        hasMore,
        capReached,
        isLoading,
        isRestoring,
        restorationTarget: restorationTargetRef.current,
        hasError,
        firstNewIndex,
        needsInitialFill,
        loadMore,
    };
}
